<template>
    <div class="sidebar" ref="element">
        <div v-if="isVisible" class="sidebar-nav" :class="{ dragable: draggable }" ref="navElement">
            <slot></slot>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { DomUtil } from "@/shared/ui/Helper";
    import { Easing, Tween } from "@tweenjs/tween.js";
    import { nextTick, onMounted, ref, watch } from "vue";

    // Props
    const props = withDefaults(
        defineProps<{
            width?: number;
            draggable?: boolean;
            showDim: boolean;
        }>(),
        {
            width: 270,
            draggable: true,
            showDim: true,
        }
    );

    // Model
    const isVisible = defineModel<boolean>();

    // html refs
    const element = ref<HTMLElement | null>(null);
    const navElement = ref<HTMLElement | null>(null);

    const $element = ref<JQuery | undefined>();
    const $nav = ref<JQuery | undefined>();
    const $dim = ref<JQuery | undefined>();

    let runningTween: any;
    let touchCtrl: HammerManager | undefined;
    let isReleasing = false;
    let currentNavPosition: number | undefined;
    const margin = parseInt(String(0.15 * props.width));
    let widthInitial = props.width + margin;

    // Watch for visibility changes
    watch(isVisible, (newValue) => {
        if (newValue) {
            openNav();
        } else {
            closeNav();
        }
    });

    // functions
    async function openNav() {
        await nextTick(); // wait next tick for html to render

        $nav.value = $(navElement.value!);
        if ($nav.value === undefined) throw new Error("nav element not found");

        $nav.value!.css({ transform: `translate3d(${-props.width}px, 0, 0)` });
        $nav.value!.css({ width: `${widthInitial}px` });
        $nav.value!.css({ left: `${-margin}px` });
        $nav.value!.css({ "padding-left": `${margin}px` });

        touchCtrl = new Hammer($nav.value[0], {
            recognizers: [[Hammer.Pan, { direction: Hammer.DIRECTION_HORIZONTAL, threshold: 0 }]],
            domEvents: false,
        });

        if (props.draggable) {
            touchCtrl.on("panstart", () => {
                if (runningTween !== undefined) {
                    runningTween.stop();
                    runningTween = undefined;
                }

                isReleasing = false;
                currentNavPosition = DomUtil.translatePosition($nav.value!).x;
            });

            touchCtrl.on("panmove", (ev) => {
                moveNav(ev.deltaX);
            });

            touchCtrl.on("panend", () => {
                if (isReleasing) return;

                isReleasing = true;
                releaseNav();
            });
        }

        if (props.showDim) {
            showDimWithNeeded();
        }

        tweenTo({ hidden: false, useBounce: true });
    }

    function showDimWithNeeded() {
        if ($dim.value === undefined) {
            const parentView = $element.value!.parents(".view");
            $dim.value = $('<div class="navdim" />')
                .prependTo(parentView)
                .click((e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    closeNav();
                });
        }
    }

    function closeNav() {
        if ($nav.value === undefined) return;
        tweenTo({ hidden: true, useBounce: false });
    }

    function onNavClosed() {
        $dim.value?.remove();
        $dim.value = undefined;

        $nav.value = undefined;
        touchCtrl!.destroy();
        touchCtrl = undefined;
        isVisible.value = false;
    }

    function moveNav(byPixel: number) {
        let newPosition = currentNavPosition! + byPixel;

        const targetOpacity = getTargetOpacity(newPosition);
        $dim.value?.css("opacity", targetOpacity);

        const targetPosition = getTargetPosition(newPosition);
        $nav.value!.css({ transform: `translate3d(${targetPosition}px, 0, 0)` });
    }

    function getTargetOpacity(newPosition: number) {
        if (newPosition > 0) return 1;

        const normalizedPosition = newPosition / props.width;
        const targetOpacity = 1 - Math.abs(normalizedPosition);

        if (targetOpacity > 1) return 1;
        if (targetOpacity < 0) return 0;

        return targetOpacity;
    }

    function getTargetPosition(newPosition: number) {
        const overdraging = newPosition > 0 ? newPosition : 0;
        const resistance = DomUtil.momentumResistance(-overdraging, 40);
        newPosition -= resistance;

        return newPosition;
    }

    function releaseNav() {
        const currentPos = DomUtil.translatePosition($nav.value!).x;
        const shouldClose = currentPos < -margin;
        tweenTo({ hidden: shouldClose, useBounce: false });
    }

    function tweenTo(options: { hidden: boolean; useBounce: boolean }) {
        const current = {
            pos: DomUtil.translatePosition($nav.value!).x,
            opacity: $dim.value?.css("opacity"),
        };

        const dest = {
            pos: options.hidden ? -props.width : 0,
            opacity: options.hidden ? 0 : 1,
        };

        runningTween = new Tween(current)
            .to(dest, 300)
            .easing(options.useBounce ? Easing.Back.Out : Easing.Exponential.Out)
            .onUpdate((f) => {
                if ($dim.value != null && f.opacity != null) {
                    $dim.value.css("opacity", f.opacity);
                }

                $nav.value!.css({ transform: `translate3d(${f.pos}px, 0, 0)` });
            })
            .start();

        runningTween.onComplete(() => {
            if (options.hidden) {
                onNavClosed();
            }

            isReleasing = false;
            runningTween = undefined;
        });

        const animate = (time?: null) => {
            if (runningTween !== undefined && runningTween.isPlaying()) {
                runningTween.update(time);
                requestAnimationFrame(animate as any);
            }
        };

        animate();
    }

    onMounted(() => {
        $element.value = $(element.value!);
    });
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_variables.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";

    .sidebar {
        > .sidebar-nav {
            background-color: #ffffff;
            position: fixed;
            top: 0;
            bottom: 0;
            z-index: 600;
            -webkit-box-shadow: 10px 10px 57px 0px rgba(72, 72, 72, 0.11);
            -moz-box-shadow: 10px 10px 57px 0px rgba(72, 72, 72, 0.11);
            box-shadow: 10px 10px 57px 0px rgba(72, 72, 72, 0.11);

            &.dragable {
                cursor: pointer;
                pointer-events: all;
            }
        }
    }
</style>
